import { PlatformIcon } from '@components/PlatformIcon';
import { SocialIcon } from '@components/SocialIcon';
import {
  IconAdd,
  IconArrowBackward,
  IconArrowDown,
  IconArrowDropDown,
  IconArrowForward,
  IconArrowOutward,
  IconAvatar,
  IconBlock,
  IconChatBubbleOff,
  IconChatDashed,
  IconCheck,
  IconCheckCircle,
  IconClear,
  IconClose,
  IconDarkMode,
  IconDatabaseCheck,
  IconDoNotDisturb,
  IconDoneAll,
  IconEdit,
  IconFilter,
  IconGenStars,
  IconGlobeSearch,
  IconGroup,
  IconLayers,
  IconLightMode,
  IconLightning,
  IconLink,
  IconLog,
  IconLogout,
  IconMail,
  IconMessageRead,
  IconOpenInNew,
  IconPaperplane,
  IconPhoneOff,
  IconProfileOff,
  IconSchedule,
  IconSearch,
  IconSearchList,
  IconSearchStars,
  IconSelectFromList,
  IconSend,
  IconUndo,
  IconVerified,
  SearchListIcon,
  SelectFromListIcon,
  MagicSearchIcon,
  EmptyStateIcon,
} from '@components/icons';
import { SectionHeader, getFigmaUrl } from './shared';
import styles from '../DemoPage.module.scss';

export const IconsSection = () => (
  <section className={styles.section}>
    <SectionHeader
      title="Icons"
      description="SVG icons from the Figma design system (43 icons total). Use with size and color props."
      figmaNodeId="6-60"
      styles={styles}
    />

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Action Icons (Row 1)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconAdd size={24} />
          <span>Add</span>
        </div>
        <div className={styles.iconItem}>
          <IconLogout size={24} />
          <span>Logout</span>
        </div>
        <div className={styles.iconItem}>
          <IconMail size={24} />
          <span>Mail</span>
        </div>
        <div className={styles.iconItem}>
          <IconSearch size={24} />
          <span>Search</span>
        </div>
        <div className={styles.iconItem}>
          <IconClear size={24} />
          <span>Clear</span>
        </div>
        <div className={styles.iconItem}>
          <IconEdit size={24} />
          <span>Edit</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Navigation Icons (Row 2)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconArrowForward size={24} />
          <span>Arrow Forward</span>
        </div>
        <div className={styles.iconItem}>
          <IconArrowBackward size={24} />
          <span>Arrow Backward</span>
        </div>
        <div className={styles.iconItem}>
          <IconLayers size={24} />
          <span>Layers</span>
        </div>
        <div className={styles.iconItem}>
          <IconLightning size={24} />
          <span>Zap/Lightning</span>
        </div>
        <div className={styles.iconItem}>
          <IconSelectFromList size={24} />
          <span>Select List</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Decorative Icons (Row 3)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconSearchStars size={24} />
          <span>Search Stars</span>
        </div>
        <div className={styles.iconItem}>
          <IconGenStars size={24} />
          <span>Gen Stars</span>
        </div>
        <div className={styles.iconItem}>
          <IconSearchList size={24} />
          <span>Search List</span>
        </div>
        <div className={styles.iconItem}>
          <IconDatabaseCheck size={24} />
          <span>DB Check</span>
        </div>
      </div>
      <p className={styles.hint}>Note: IconLoading is a PNG spinner, not included as SVG.</p>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>UI Control Icons (Row 4)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconClose size={24} />
          <span>Close</span>
        </div>
        <div className={styles.iconItem}>
          <IconArrowDropDown size={24} />
          <span>Arrow Drop</span>
        </div>
        <div className={styles.iconItem}>
          <IconArrowDown size={24} />
          <span>Arrow Down</span>
        </div>
        <div className={styles.iconItem}>
          <IconCheck size={24} />
          <span>Check</span>
        </div>
        <div className={styles.iconItem}>
          <IconFilter size={24} />
          <span>Filter List</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Communication Icons (Row 5)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconAvatar size={24} />
          <span>Avatar</span>
        </div>
        <div className={styles.iconItem}>
          <IconMessageRead size={24} />
          <span>Message Read</span>
        </div>
        <div className={styles.iconItem}>
          <IconGroup size={24} />
          <span>Group</span>
        </div>
        <div className={styles.iconItem}>
          <IconChatDashed size={24} />
          <span>Chat Dashed</span>
        </div>
        <div className={styles.iconItem}>
          <IconPhoneOff size={24} />
          <span>Phone Off</span>
        </div>
        <div className={styles.iconItem}>
          <IconProfileOff size={24} />
          <span>Profile Off</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Utility Icons (Row 6)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconOpenInNew size={24} />
          <span>Open In New</span>
        </div>
        <div className={styles.iconItem}>
          <IconGlobeSearch size={24} />
          <span>Globe Search</span>
        </div>
        <div className={styles.iconItem}>
          <IconLink size={24} />
          <span>Link</span>
        </div>
        <div className={styles.iconItem}>
          <IconLightMode size={24} />
          <span>Light Mode</span>
        </div>
        <div className={styles.iconItem}>
          <IconDarkMode size={24} />
          <span>Dark Mode</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Status Icons (Row 7-8)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconVerified size={24} />
          <span>Verified</span>
        </div>
        <div className={styles.iconItem}>
          <IconDoneAll size={24} />
          <span>Done All</span>
        </div>
        <div className={styles.iconItem}>
          <IconCheckCircle size={24} />
          <span>Check Circle</span>
        </div>
        <div className={styles.iconItem}>
          <IconBlock size={24} />
          <span>Block</span>
        </div>
        <div className={styles.iconItem}>
          <IconDoNotDisturb size={24} />
          <span>Do Not Disturb</span>
        </div>
        <div className={styles.iconItem}>
          <IconChatBubbleOff size={24} />
          <span>Chat Bubble Off</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Action Icons (Row 8-9)</h3>
      <div className={styles.iconGrid}>
        <div className={styles.iconItem}>
          <IconLog size={24} />
          <span>Log</span>
        </div>
        <div className={styles.iconItem}>
          <IconSchedule size={24} />
          <span>Schedule</span>
        </div>
        <div className={styles.iconItem}>
          <IconPaperplane size={24} />
          <span>Paperplane</span>
        </div>
        <div className={styles.iconItem}>
          <IconSend size={24} />
          <span>Send</span>
        </div>
        <div className={styles.iconItem}>
          <IconUndo size={24} />
          <span>Undo</span>
        </div>
        <div className={styles.iconItem}>
          <IconArrowOutward size={24} />
          <span>Arrow Outward</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Empty State Icons (Large)</h3>
      <div className={styles.iconGridLarge}>
        <div className={styles.iconItemLarge}>
          <SearchListIcon width={64} height={48} />
          <span>Search List</span>
        </div>
        <div className={styles.iconItemLarge}>
          <SelectFromListIcon width={64} height={48} />
          <span>Select From List</span>
        </div>
        <div className={styles.iconItemLarge}>
          <MagicSearchIcon width={64} height={64} />
          <span>Magic Search</span>
        </div>
        <div className={styles.iconItemLarge}>
          <EmptyStateIcon width={64} height={48} />
          <span>Empty State</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Platform Icons</h3>
      <p className={styles.hint} style={{ marginTop: 0, marginBottom: '16px' }}>
        E-commerce platform logos. Fixed height of 24px with auto-adjusting width.
        <a
          href={getFigmaUrl('380-4335')}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: '8px', color: 'var(--color-primary-base)' }}
        >
          View in Figma
        </a>
      </p>
      <div className={styles.platformIconDemo}>
        <div className={styles.platformIconItem}>
          <PlatformIcon platform="framer" />
          <span>framer</span>
        </div>
        <div className={styles.platformIconItem}>
          <PlatformIcon platform="magento" />
          <span>magento</span>
        </div>
        <div className={styles.platformIconItem}>
          <PlatformIcon platform="shopify" />
          <span>shopify</span>
        </div>
        <div className={styles.platformIconItem}>
          <PlatformIcon platform="woocommerce" />
          <span>woocommerce</span>
        </div>
        <div className={styles.platformIconItem}>
          <PlatformIcon platform="wordpress" />
          <span>wordpress</span>
        </div>
      </div>
    </div>

    <div className={styles.subsection}>
      <h3 className={styles.subsectionTitle}>Social Icons</h3>
      <p className={styles.hint} style={{ marginTop: 0, marginBottom: '16px' }}>
        Social media provider icons. Fixed size of 32x32px.
        <a
          href={getFigmaUrl('365-3403')}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: '8px', color: 'var(--color-primary-base)' }}
        >
          View in Figma
        </a>
      </p>
      <div className={styles.socialIconDemo}>
        <div className={styles.socialIconItem}>
          <SocialIcon provider="facebook" />
          <span>facebook</span>
        </div>
        <div className={styles.socialIconItem}>
          <SocialIcon provider="instagram" />
          <span>instagram</span>
        </div>
        <div className={styles.socialIconItem}>
          <SocialIcon provider="tiktok" />
          <span>tiktok</span>
        </div>
        <div className={styles.socialIconItem}>
          <SocialIcon provider="x" />
          <span>x</span>
        </div>
        <div className={styles.socialIconItem}>
          <SocialIcon provider="whatsapp" />
          <span>whatsapp</span>
        </div>
        <div className={styles.socialIconItem}>
          <SocialIcon provider="youtube" />
          <span>youtube</span>
        </div>
        <div className={styles.socialIconItem}>
          <SocialIcon provider="linkedin" />
          <span>linkedin</span>
        </div>
      </div>
    </div>
  </section>
);
